import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { AvailableSettings } from 'types/Settings'
import { useDomainPolicyAccess } from 'lib/hooks/useDomainPolicyAccess'

import {
  getAccountSettings,
  getDomainSettings,
  resetAccountAndDomainSettings
} from 'redux/features/settings/settingsSlice'
import { isSuccess } from 'redux/toolkit/api'
import { useInboundSettingsRights } from 'components/libs/userRights/pages/useInboundSettingsRights'
import { isMyFeatureOn } from 'lib/splitio'
import { FEATURES } from 'lib/splitioFeatures'

export interface State {
  canChangeLanguagePolicies: boolean
  hasPageChanges: boolean
  hasNoDomainPolicyAccess: boolean
  isLanguagePolicyExemptionsOn: boolean
}

export interface EventHandlers {
  helpConfig: {
    isOpen: boolean
    onHelpClick: () => void
    onCloseHelp: () => void
  }
  setIsGeoipTableDirty: Dispatch<SetStateAction<boolean>>
  setIsLanguageTableDirty: Dispatch<SetStateAction<boolean>>
  setIsIpAExemptTableDirty: Dispatch<SetStateAction<boolean>>
  setIsSenderExemptTableDirty: Dispatch<SetStateAction<boolean>>
  setIsLanguageExemptTableDirty: Dispatch<SetStateAction<boolean>>
}

export type UseRegionalPoliciesLogic = [State, EventHandlers]

const SETTINGS_LIST = [
  AvailableSettings.GEOIP_POLICIES,
  AvailableSettings.GEOIP_SENDER_NAME_EXEMPT_POLICIES,
  AvailableSettings.GEOIP_IP_ADDRESS_EXEMPT_POLICIES,
  AvailableSettings.LANGUAGE_POLICIES
]

export const useRegionalPoliciesLogic = (): UseRegionalPoliciesLogic => {
  const isBulkExemptionsOn = isMyFeatureOn(FEATURES.EGD_React_Additional_Exemptions)
  const dispatch = useAppDispatch()
  const [isHelpDialogOpened, setIsHelpDialogOpened] = useState<boolean>(false)

  const { accessTokenObject, isUpdateAccountSettingsSuccess, isUpdateDomainSettingsSuccess } = useAppSelector(
    _stores => ({
      accessTokenObject: _stores.auth.accessTokenObject,
      isUpdateAccountSettingsSuccess: isSuccess(_stores.settings.updateAccountSettingsApiStatus),
      isUpdateDomainSettingsSuccess: isSuccess(_stores.settings.updateDomainSettingsApiStatus)
    })
  )
  const { canChangeLanguagePolicies } = useInboundSettingsRights()
  // Domain policy access check - only applies in domain-manage mode (when pdDomainId is set)
  const pdDomainId = accessTokenObject?.pdDomainId
  const isInDomainManageMode = !!pdDomainId
  const { hasNoDomainPolicyAccess: rawHasNoDomainPolicyAccess } = useDomainPolicyAccess(Number(pdDomainId) || 0)
  // Only block access when in domain-manage mode AND user has no R or U access
  const hasNoDomainPolicyAccess = isInDomainManageMode && rawHasNoDomainPolicyAccess
  const [isGeoipTableDirty, setIsGeoipTableDirty] = useState<boolean>(false)
  const [isLanguageTableDirty, setIsLanguageTableDirty] = useState<boolean>(false)
  const [isIpExemptTableDirty, setIsIpAExemptTableDirty] = useState<boolean>(false)
  const [isSenderExemptTableDirty, setIsSenderExemptTableDirty] = useState<boolean>(false)
  const [isLanguageExemptTableDirty, setIsLanguageExemptTableDirty] = useState<boolean>(false)

  // Build settings list for fetching
  const getSettingsList = useCallback(() => {
    const settingsList = canChangeLanguagePolicies
      ? [...SETTINGS_LIST]
      : SETTINGS_LIST.filter(setting => setting !== AvailableSettings.LANGUAGE_POLICIES)
    if (isBulkExemptionsOn && canChangeLanguagePolicies) {
      settingsList.push(AvailableSettings.LANGUAGE_EXEMPT)
    }
    return settingsList
  }, [isBulkExemptionsOn, canChangeLanguagePolicies])

  // init
  useEffect(() => {
    if (hasNoDomainPolicyAccess) {
      return
    }
    const settingsList = getSettingsList()
    if (accessTokenObject?.pdDomainId) {
      dispatch(getDomainSettings({ domainId: accessTokenObject?.pdDomainId, settings: settingsList }))
    } else {
      dispatch(getAccountSettings(settingsList))
    }
    // eslint-disable-next-line
  }, [hasNoDomainPolicyAccess])

  const onHelpClick = useCallback(() => {
    setIsHelpDialogOpened(true)
  }, [])

  const onCloseHelp = useCallback(() => {
    setIsHelpDialogOpened(false)
  }, [])

  // update state on add/remove
  useEffect(() => {
    if (isUpdateAccountSettingsSuccess || isUpdateDomainSettingsSuccess) {
      const settingsList = getSettingsList()
      if (accessTokenObject?.pdDomainId) {
        dispatch(getDomainSettings({ domainId: accessTokenObject?.pdDomainId, settings: settingsList }))
      } else {
        dispatch(getAccountSettings(settingsList))
      }
    }
  }, [dispatch, accessTokenObject, isUpdateAccountSettingsSuccess, isUpdateDomainSettingsSuccess, getSettingsList])

  // unmount
  useEffect(
    () => () => {
      dispatch(resetAccountAndDomainSettings())
    },
    [dispatch]
  )

  return useMemo(
    () => [
      {
        canChangeLanguagePolicies,
        hasPageChanges:
          isGeoipTableDirty ||
          isLanguageTableDirty ||
          isIpExemptTableDirty ||
          isSenderExemptTableDirty ||
          isLanguageExemptTableDirty,
        hasNoDomainPolicyAccess,
        isLanguagePolicyExemptionsOn: isBulkExemptionsOn && canChangeLanguagePolicies && !isInDomainManageMode
      },
      {
        setIsGeoipTableDirty,
        setIsLanguageTableDirty,
        setIsIpAExemptTableDirty,
        setIsSenderExemptTableDirty,
        setIsLanguageExemptTableDirty,
        helpConfig: {
          isOpen: isHelpDialogOpened,
          onHelpClick,
          onCloseHelp
        }
      }
    ],
    [
      canChangeLanguagePolicies,
      isHelpDialogOpened,
      onHelpClick,
      onCloseHelp,
      isGeoipTableDirty,
      isLanguageTableDirty,
      isIpExemptTableDirty,
      isSenderExemptTableDirty,
      isLanguageExemptTableDirty,
      setIsGeoipTableDirty,
      setIsLanguageTableDirty,
      setIsIpAExemptTableDirty,
      setIsSenderExemptTableDirty,
      setIsLanguageExemptTableDirty,
      hasNoDomainPolicyAccess,
      isBulkExemptionsOn,
      isInDomainManageMode
    ]
  )
}
